if (!customElements.get('gs-bundle-builder')) {
  class GsBundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.i18n = JSON.parse(this.querySelector('[data-gs-bundle-i18n]').textContent);
      this.collectionHandle = this.dataset.collectionHandle;
      this.collectionHandles = (this.dataset.collectionHandles || this.collectionHandle || '')
        .split(',')
        .map(handle => handle.trim())
        .filter(Boolean);
      this.collectionLanguages = (this.dataset.collectionLanguages || '')
        .split(',')
        .map(language => language.trim().toLowerCase())
        .filter(Boolean);
      const thumbnailData = this.querySelector('[data-gs-bundle-thumbnails]');
      this.thumbnailMap = thumbnailData ? JSON.parse(thumbnailData.textContent) : {};
      const artworkData = this.querySelector('[data-gs-bundle-artwork]');
      this.artworkMap = artworkData ? JSON.parse(artworkData.textContent) : {};
      this.currency = this.dataset.moneyCode || 'EUR';
      this.pageMode = this.dataset.pageMode === 'true';
      this.offerPilot = this.dataset.offerPilot === 'true' && !this.pageMode;
      const offerText = this.querySelector('[data-gs-offer-i18n]');
      if (this.offerPilot && offerText) Object.assign(this.i18n, JSON.parse(offerText.textContent));
      this.currentProductHandle = this.dataset.currentProductHandle || '';
      this.products = [];
      this.newProductIds = new Set();
      this.slots = [];
      this.query = '';
      this.filter = 'all';
      this.modal = this.querySelector('[data-gs-bundle-modal]');
      this.grid = this.querySelector('[data-gs-bundle-grid]');
      this.loading = this.querySelector('[data-gs-bundle-loading]');
      this.slotsEl = this.querySelector('[data-gs-bundle-slots]');
      this.totalsEl = this.querySelector('[data-gs-bundle-totals]');
      this.progressEl = this.querySelector('[data-gs-bundle-progress]');
      this.statusEl = this.querySelector('[data-gs-bundle-status]');
      this.atc = this.querySelector('[data-gs-bundle-atc]');
      this.errorEl = this.querySelector('[data-gs-bundle-error]');
      this.filterEl = this.querySelector('[data-gs-bundle-filters]');
      this.lastFocus = null;
      this.notice = this.modal.querySelector('[data-gs-target-notice]');
      this.inline = this.offerPilot && this.closest('[data-section-type="product"]')?.querySelector('[data-gs-offer-inline]');
      if (this.inline) {
        this.inline.appendChild(this.slotsEl.closest('.gs-bundle__summary'));
        this.modal.dataset.pickerOnly = 'true';
      }
      this.notice?.querySelector('[data-gs-target-confirm]')?.addEventListener('click', () => {
        if (this.pendingTarget && this.slots.length <= this.pendingTarget) this.configureOffer(this.pendingTarget);
        (this.inline ? this.closest('[data-section-type="product"]').querySelector('[data-target="3"]') : this.modal.querySelector('[data-gs-bundle-close]'))?.focus();
      });
      this.notice?.querySelector('[data-gs-target-cancel]')?.addEventListener('click', () => {
        this.pendingTarget = null;
        this.renderSummary();
        (this.inline ? this.closest('[data-section-type="product"]').querySelector('[data-target="6"]') : this.modal.querySelector('[data-gs-bundle-close]'))?.focus();
      });
      if (!this.pageMode) document.querySelectorAll('.sezane-offer, #gs-format-banner').forEach(block => { block.hidden = true; });
      this.querySelector('[data-gs-pack="single"]')?.addEventListener('click', () => this.selectPack('single'));
      this.querySelector('[data-gs-pack="bundle"]')?.addEventListener('click', () => this.selectPack('bundle'));
      this.querySelector('[data-gs-bundle-open]')?.addEventListener('click', () => {
        this.configureOffer(3);
        this.open();
      });
      this.querySelector('[data-gs-bundle-close]')?.addEventListener('click', () => this.close());
      this.modal.addEventListener('click', event => { if (event.target === this.modal) this.close(); });
      this.querySelector('[data-gs-bundle-search]').addEventListener('input', event => { this.query = event.target.value.trim().toLowerCase(); this.renderGrid(); });
      this.filterEl.addEventListener('click', event => {
        const button = event.target.closest('[data-filter]');
        if (!button) return;
        this.filter = button.dataset.filter;
        this.renderFilters();
        this.renderGrid();
      });
      this.grid.addEventListener('click', event => {
        const button = event.target.closest('[data-add-product]');
        if (!button) return;
        this.toggleProduct(button.dataset.addProduct);
      });
      this.slotsEl.addEventListener('click', event => {
        const picker = event.target.closest('[data-gs-pick-slot]');
        if (picker) { this.open(); return; }
        const button = event.target.closest('[data-remove-slot]');
        if (button) this.removeSlot(Number(button.dataset.removeSlot));
      });
      this.slotsEl.addEventListener('keydown', event => {
        const picker = event.target.closest('[data-gs-pick-slot]');
        if (!picker || (event.key !== 'Enter' && event.key !== ' ')) return;
        event.preventDefault();
        this.open();
      });
      this.slotsEl.addEventListener('change', event => {
        const select = event.target.closest('[data-slot-option]');
        if (select) this.changeOption(Number(select.dataset.slotIndex), Number(select.dataset.optionIndex), select.value);
      });
      this.atc.addEventListener('click', () => this.addToCart());
      this._keydown = event => this.onKeydown(event);
      document.addEventListener('keydown', this._keydown);
      this.renderFilters();
      this.renderSummary();
      if (this.pageMode) this.open();
    }

    disconnectedCallback() { if (this._keydown) document.removeEventListener('keydown', this._keydown); }

    get targetCount() { return this._targetCount || 3; }

    async expandOffer(target) {
      if (!this.inline || this._adding) return;
      this.configureOffer(target);
      this.inline.hidden = false;
      this.closest('[data-section-type="product"]').dataset.gsOfferMode = 'bundle';
      const nativeId = this.closest('[data-section-type="product"]').querySelector('[data-product-select]')?.value;
      if (nativeId && nativeId !== this._pdpVariantId) {
        this._pdpVariantId = nativeId;
        const slot = this.slots.find(s => s.product.id === this.dataset.currentProductId);
        const variant = slot?.product.variants.find(v => v.available && String(v.id) === nativeId);
        if (variant) { slot.variant = variant; slot.selections = [...variant.options]; }
      }
      if (!this.products.length) {
        this._catalogPromise = this._catalogPromise || this.loadProducts().finally(() => { this._catalogPromise = null; });
        await this._catalogPromise;
      }
      if (!this._inlineSeeded) { this.seedCurrent(); this._inlineSeeded = this.slots.length > 0; }
      this.renderSummary();
    }

    configureOffer(target) {
      if (this._adding) return false;
      if (![3, 6].includes(target) || (target === 6 && (this.dataset.offerPilot !== 'true' || this.pageMode))) return false;
      if (this.slots.length > target) {
        this.pendingTarget = target;
        this.renderSummary();
        return false;
      }
      this.pendingTarget = null;
      this._targetCount = target;
      this.modal.dataset.offerTarget = String(target);
      this.renderSummary();
      this.renderGrid();
      this.dispatchEvent(new CustomEvent('gs:offer-target', { bubbles: true, detail: { target } }));
      return true;
    }

    seedCurrent() {
      if (!this.offerPilot || !this.products.length) return;
      const current = this.products.find(p => p.id === this.dataset.currentProductId || p.handle === this.currentProductHandle);
      const variant = current?.variants.find(v => v.available && String(v.id) === this._pdpVariantId);
      if (!current || !variant) { this.showError(this.i18n.seedError); return; }
      // An intentional removal/variant edit is retained. open() synchronizes an
      // existing current slot only when the native variant has actually changed.
      if (!this.slots.length) this.slots.push({ product: current, variant, selections: [...variant.options] });
      this.renderSummary();
      this.renderGrid();
    }

    selectPack(pack) {
      this.querySelectorAll('[data-gs-pack]').forEach(button => {
        const selected = button.dataset.gsPack === pack;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-checked', selected ? 'true' : 'false');
      });
      if (pack === 'bundle') this.open();
    }

    async open() {
      this.lastFocus = document.activeElement;
      // Read the native single-canvas selection; never write to its form.
      const nativeId = this.closest('[data-section-type="product"]')?.querySelector('[data-product-select]')?.value;
      if (nativeId && nativeId !== this._pdpVariantId) {
        this._pdpVariantId = nativeId;
        const currentSlot = this.slots.find(slot => (this.offerPilot && slot.product.id === this.dataset.currentProductId) || slot.product.handle === this.currentProductHandle);
        const variant = currentSlot?.product.variants.find(item => item.available && String(item.id) === nativeId);
        if (variant) {
          currentSlot.variant = variant;
          currentSlot.selections = [...variant.options];
          this.renderSummary();
        }
      }
      // Product sections in Motion create their own stacking contexts. Portaling the
      // dialog to <body> lets it sit above the fixed announcement/header layers.
      if (!this.pageMode && this.modal.parentNode !== document.body) {
        this._modalHome = this;
        document.body.appendChild(this.modal);
      }
      this.modal.hidden = false;
      if (!this.pageMode) {
        document.documentElement.classList.add('gs-bundle-open');
        document.body.classList.add('gs-bundle-open');
        this.modal.querySelector('[data-gs-bundle-close]')?.focus();
      }
      if (!this.products.length) await this.loadProducts();
      if (this.offerPilot && !this.inline) this.seedCurrent();
    }

    close() {
      if (this.pageMode) return;
      this.modal.hidden = true;
      document.documentElement.classList.remove('gs-bundle-open');
      document.body.classList.remove('gs-bundle-open');
      if (this._modalHome && this.modal.parentNode === document.body) this._modalHome.appendChild(this.modal);
      this.selectPack('single');
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    }

    onKeydown(event) {
      if (this.modal.hidden) return;
      if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
      if (event.key !== 'Tab') return;
      const focusable = [...this.modal.querySelectorAll('button:not([disabled]),input:not([disabled]),select:not([disabled]),[href],[tabindex]:not([tabindex="-1"])')].filter(el => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    async loadProducts() {
      this.loading.hidden = false;
      this.loading.textContent = this.i18n.loading;
      // Only accompany the actual catalog request; no timer or artificial wait.
      const floralSource = document.getElementById('gs-floral-loader');
      if (floralSource) {
        const floral = floralSource.cloneNode(true);
        floral.removeAttribute('id');
        floral.className = 'gs-floral-local';
        floral.hidden = false;
        floral.setAttribute('aria-label', this.i18n.loading);
        this.loading.replaceChildren(floral);
      }
      try {
        const catalogs = await Promise.all(this.collectionHandles.map(async handle => {
          const response = await fetch(`/collections/${encodeURIComponent(handle)}/products.json?limit=250`, { credentials: 'same-origin' });
          if (!response.ok) throw new Error(`${handle}: HTTP ${response.status}`);
          const data = await response.json();
          return data.products || [];
        }));
        const scopedCatalogs = catalogs.map((products, index) => {
          const language = this.collectionLanguages[index];
          if (!language) return products;
          return products.filter(product => (product.tags || []).some(tag => String(tag).toLowerCase() === `lang:${language}`));
        });
        const newCollectionIndex = this.collectionHandles.indexOf('je-me-choisis');
        const explicitNew = newCollectionIndex >= 0 ? (scopedCatalogs[newCollectionIndex] || []) : scopedCatalogs.flat().filter(product => (product.tags || []).some(tag => /^(badge:)?new[_ -]?arrival$/i.test(String(tag))));
        this.newProductIds = new Set(explicitNew.map(product => String(product.id)));
        const uniqueProducts = new Map();
        scopedCatalogs.flat().forEach(product => {
          const key = String(product.id);
          if (!uniqueProducts.has(key)) uniqueProducts.set(key, product);
        });
        this.products = [...uniqueProducts.values()]
          .filter(product => !/[ée]dition\s+.*mod[èe]le/i.test(product.vendor || ''))
          .filter(product => (product.variants || []).some(variant => variant.available))
          .map(product => this.normalizeProduct(product));
        this.loading.hidden = true;
        if (!this.offerPilot && !this.pageMode && this.currentProductHandle && this.slots.length === 0) {
          const current = this.products.find(item => item.handle === this.currentProductHandle);
          const variant = current && (current.variants.find(item => item.available && String(item.id) === this._pdpVariantId) || this.defaultVariant(current));
          if (current && variant) this.slots.push({ product: current, variant, selections: [...variant.options] });
          this.renderSummary();
        }
        this.renderGrid();
      } catch (error) {
        console.error('[GS Motion trio] Product loading failed', error);
        this.loading.hidden = false;
        this.loading.textContent = this.i18n.loadError;
      }
    }

    normalizeProduct(product) {
      const options = (product.options || []).map((option, index) => ({ name: option.name || `Option ${index + 1}`, values: option.values || [], index }));
      const directArtwork = !this.pageMode ? this.artworkMap[String(product.id)] : null;
      const gridThumbnail = (directArtwork && directArtwork.src480) || this.thumbnailMap[String(product.id)] || '';
      const image = gridThumbnail || (product.images && product.images[0] && (product.images[0].src || product.images[0])) || (product.image && product.image.src) || '';
      const variants = (product.variants || []).map(variant => ({
        id: variant.id,
        available: variant.available,
        price: Math.round(parseFloat(variant.price || 0) * 100),
        compareAt: Math.round(parseFloat(variant.compare_at_price || 0) * 100),
        options: [variant.option1, variant.option2, variant.option3].slice(0, options.length)
      }));
      return { id: String(product.id), handle: product.handle, title: product.title, vendor: product.vendor || 'Giulia Schiaparelli', tags: (product.tags || []).map(tag => String(tag).toLowerCase()), image, directArtwork, options, variants };
    }

    renderFilters() {
      const filters = [
        ['all', this.i18n.all],
        ['new', this.i18n.newArrivals],
        ['best', this.i18n.bestSellers],
        ['floral', this.i18n.flowers],
        ['music', this.i18n.music]
      ];
      this.filterEl.innerHTML = filters.map(([key, label]) => `<button type="button" class="gs-bundle__filter${this.filter === key ? ' is-active' : ''}" data-filter="${key}">${this.escape(label)}</button>`).join('');
    }

    productMatches(product) {
      const haystack = `${product.title} ${product.tags.join(' ')}`.toLowerCase();
      if (this.query && !haystack.includes(this.query)) return false;
      if (this.filter === 'new' && !this.newProductIds.has(product.id)) return false;
      if (this.filter === 'best' && !haystack.includes('best seller')) return false;
      if (this.filter === 'floral' && !/(floral|fleur)/.test(haystack)) return false;
      if (this.filter === 'music' && !/(musique|music|vinyl)/.test(haystack)) return false;
      return true;
    }

    renderGrid() {
      if (!this.products.length) return;
      const visible = this.products.filter(product => this.productMatches(product));
      if (!visible.length) {
        this.grid.innerHTML = `<p class="gs-bundle__empty-results">${this.escape(this.i18n.noResults)}</p>`;
        return;
      }
      const full = this.slots.length >= this.targetCount;
      const fullLabel = this.targetCount === 6 ? this.i18n.fullSix : this.i18n.full;
      const cardImageWidth = window.innerWidth < 750 ? 360 : 480;
      const cards = visible.map((product, cardIndex) => {
        const selected = this.slots.some(slot => slot.product.id === product.id);
        const variant = this.defaultVariant(product);
        const compareAt = variant && variant.compareAt > variant.price ? `<s>${this.money(variant.compareAt)}</s>` : '';
        const direct = product.directArtwork;
        const directKey = cardImageWidth === 360 ? '360' : '480';
        const imageSrc = direct ? direct[`src${directKey}`] : this.resizeImage(product.image, cardImageWidth);
        const imageWidth = direct ? direct[`width${directKey}`] : cardImageWidth;
        const imageHeight = direct ? direct[`height${directKey}`] : Math.round(cardImageWidth * 1.25);
        return `<article class="gs-bundle__card${direct ? ' is-direct-art' : ''}">
          <div class="gs-bundle__card-media">
            ${imageSrc ? `<img class="${direct ? 'gs-bundle__direct-art' : ''}" src="${this.escapeAttr(imageSrc)}" alt="${this.escapeAttr(product.title)}" loading="${cardIndex < 2 ? 'eager' : 'lazy'}" fetchpriority="${cardIndex === 0 ? 'high' : 'auto'}" decoding="async" width="${imageWidth}" height="${imageHeight}">` : ''}
            <button type="button" class="gs-bundle__card-add${selected ? ' is-added' : ''}" data-add-product="${this.escapeAttr(product.id)}" aria-label="${selected ? this.escapeAttr(this.i18n.added) : full ? this.escapeAttr(fullLabel) : this.escapeAttr(this.i18n.add)}" ${full && !selected ? 'disabled' : ''}>${selected ? this.escape(this.i18n.added) : full ? this.escape(fullLabel) : this.escape(this.i18n.add)}</button>
          </div>
          <div class="gs-bundle__card-info">
            <h3 class="gs-bundle__card-title">${this.escape(product.title)}</h3>
            <div class="gs-bundle__card-footer"><span class="gs-bundle__card-artist">${this.escape(product.vendor)}</span><span class="gs-bundle__card-price">${compareAt}<strong>${variant ? this.money(variant.price) : ''}</strong></span></div>
          </div>
        </article>`;
      });
      if (!this.offerPilot && this.filter === 'all' && !this.query && cards.length > 1) {
        const isFrench = document.documentElement.lang.startsWith('fr');
        cards.splice(1, 0, `<article class="gs-bundle__promo-card" aria-label="${this.escapeAttr(this.i18n.promo_aria || (isFrench ? 'La troisième toile est offerte' : 'Your third canvas is free'))}"><span class="gs-bundle__promo-stars" aria-hidden="true">✦</span><p class="gs-bundle__promo-script">${this.escape(this.i18n.promo_first || (isFrench ? 'La troisième' : 'Your third'))}</p><p class="gs-bundle__promo-copy">${this.escape(this.i18n.promo_second || (isFrench ? 'toile est offerte' : 'canvas is free'))}</p></article>`);
      }
      this.grid.innerHTML = cards.join('');
    }

    toggleProduct(id) {
      if (this._adding) return;
      const index = this.slots.findIndex(slot => slot.product.id === String(id));
      if (index >= 0) { if (!this.inline) this.removeSlot(index); return; }
      if (this.slots.length >= this.targetCount) return;
      const product = this.products.find(item => item.id === String(id));
      if (!product) return;
      const variant = this.defaultVariant(product);
      if (!variant) return;
      this.slots.push({ product, variant, selections: [...variant.options] });
      this.renderSummary();
      this.renderGrid();
      if (this.inline && !this.modal.hidden) {
        this.close();
        this.slotsEl.querySelector(`[data-slot-index="${this.slots.length - 1}"]`)?.focus();
      }
    }

    defaultVariant(product) {
      return product.variants.find(variant => variant.available && variant.options.some(value => /toile seule|canvas only|solo lienzo|lienzo solo|nur leinwand|alleen canvas|solo tela/i.test(value || '')) && variant.options.some(value => /30\s*[x×]\s*40/i.test(value || '')))
        || product.variants.find(variant => variant.available && variant.options.some(value => /toile seule|canvas only|solo lienzo|lienzo solo|nur leinwand|alleen canvas|solo tela/i.test(value || '')))
        || product.variants.find(variant => variant.available);
    }

    removeSlot(index) {
      if (this._adding) return;
      this.slots.splice(index, 1);
      this.hideError();
      this.renderSummary();
      this.renderGrid();
      if (this.inline) this.slotsEl.querySelector('[data-gs-pick-slot]')?.focus();
    }

    changeOption(slotIndex, optionIndex, value) {
      if (this._adding) return;
      const slot = this.slots[slotIndex];
      if (!slot) return;
      const next = [...slot.selections];
      next[optionIndex] = value;
      let variant = slot.product.variants.find(item => item.available && item.options.every((option, index) => option === next[index]));
      if (!variant) variant = slot.product.variants.find(item => item.available && item.options[optionIndex] === value);
      if (!variant) return;
      slot.variant = variant;
      slot.selections = [...variant.options];
      this.hideError();
      this.renderSummary();
      if (this.inline) this.slotsEl.querySelector(`[data-slot-index="${slotIndex}"][data-option-index="${optionIndex}"]`)?.focus();
    }

    renderSummary() {
      const complete = this.slots.length === this.targetCount;
      const offerFreeCount = this.targetCount === 6 ? 3 : 1;
      const reservedFreeStart = this.targetCount - offerFreeCount;
      const freeIndexes = new Set();
      if (this.offerPilot) {
        if (complete) {
          this.slots
            .map((slot, index) => ({ index, price: slot.variant.price, reserved: index >= reservedFreeStart ? 1 : 0 }))
            .sort((a, b) => a.price - b.price || b.reserved - a.reserved || b.index - a.index)
            .slice(0, offerFreeCount)
            .forEach(item => freeIndexes.add(item.index));
        } else {
          for (let index = reservedFreeStart; index < this.targetCount; index += 1) freeIndexes.add(index);
        }
      } else if (complete) {
        freeIndexes.add(this.slots.reduce((best, slot, index, array) => slot.variant.price <= array[best].variant.price ? index : best, 0));
      }
      const freeIndex = !this.offerPilot && freeIndexes.size ? [...freeIndexes][0] : -1;
      const labels = this.offerPilot ? Array.from({ length: this.targetCount }, (_, i) => this.i18n.slot.replace('%COUNT%', i + 1)) : [this.i18n.first, this.i18n.second, this.i18n.third];
      this.slotsEl.innerHTML = labels.map((label, index) => {
        const slot = this.slots[index];
        const isFree = freeIndexes.has(index);
        const appliedFree = complete && isFree;
        if (!slot) return this.inline ? `<div class="gs-bundle__slot is-empty${isFree ? ' is-free is-free-pending' : ''}" role="button" tabindex="0" data-gs-pick-slot="${index}" aria-label="${this.escapeAttr(this.i18n.chooseArtwork)} ${index + 1}${isFree ? ` — ${this.escapeAttr(this.i18n.free)}` : ''}"><span class="gs-bundle__slot-placeholder"><span aria-hidden="true">+</span>${this.escape(this.i18n.add)}</span><div><p class="gs-bundle__slot-title">${this.escape(label)}</p><p class="gs-bundle__slot-empty">${this.escape(this.i18n.chooseArtwork)}</p></div>${isFree ? `<span class="gs-bundle__free-badge">${this.escape(this.i18n.free)}</span>` : ''}</div>` : `<div class="gs-bundle__slot is-empty"><div class="gs-bundle__slot-placeholder"></div><div><p class="gs-bundle__slot-kicker">${this.escape(label)}</p><p class="gs-bundle__slot-title">${this.escape(this.i18n.empty)}</p><p class="gs-bundle__slot-empty">${this.escape(this.i18n.chooseArtwork)}</p></div></div>`;
        const orderedOptions = slot.product.options
          .map((option, optionIndex) => ({ option, optionIndex }))
          .sort((a, b) => this.optionRank(a.option, slot.product) - this.optionRank(b.option, slot.product));
        const controls = orderedOptions.map(({ option, optionIndex }) => {
          const values = [...new Set(slot.product.variants.filter(variant => variant.available).map(variant => variant.options[optionIndex]).filter(Boolean))];
          return `<label>${this.escape(this.optionLabel(option, values))}<select data-slot-option data-slot-index="${index}" data-option-index="${optionIndex}">${values.map(value => `<option value="${this.escapeAttr(value)}"${slot.selections[optionIndex] === value ? ' selected' : ''}>${this.escape(this.displayOption(value, slot.product, optionIndex))}</option>`).join('')}</select></label>`;
        }).join('');
        return `<div class="gs-bundle__slot${isFree ? ' is-free' : ''}">
          ${slot.product.image ? `<img class="gs-bundle__slot-image" src="${this.escapeAttr(this.resizeImage(slot.product.image, 360))}" alt="" width="180" height="220">` : '<div class="gs-bundle__slot-placeholder"></div>'}
          <div><p class="gs-bundle__slot-kicker">${this.escape(label)}</p><p class="gs-bundle__slot-title">${this.escape(slot.product.title)}</p><button type="button" class="gs-bundle__slot-remove" data-remove-slot="${index}">${this.escape(this.i18n.remove)}</button><div class="gs-bundle__slot-controls">${controls}</div><div class="gs-bundle__slot-price">${appliedFree ? `<s>${this.money(slot.variant.price)}</s><span>${this.money(0)}</span>` : `<span>${this.money(slot.variant.price)}</span>`}</div></div>
          ${isFree ? `<span class="gs-bundle__free-badge">${this.escape(this.i18n.free)}</span>` : ''}
        </div>`;
      }).join('');
      const subtotal = this.slots.reduce((sum, slot) => sum + slot.variant.price, 0);
      const offerSaving = complete && this.offerPilot
        ? this.slots.map(slot => slot.variant.price).sort((a, b) => a - b).slice(0, offerFreeCount).reduce((sum, price) => sum + price, 0)
        : 0;
      const saving = this.offerPilot ? offerSaving : freeIndex >= 0 ? this.slots[freeIndex].variant.price : 0;
      const total = subtotal - saving;
      this.totalsEl.innerHTML = this.offerPilot && complete
        ? `<div class="gs-bundle__total-line"><span>${this.escape(this.i18n.subtotal)}</span><s>${this.money(subtotal)}</s></div><div class="gs-bundle__total-line gs-bundle__total-line--discount"><span>${this.escape(this.targetCount === 6 ? this.i18n.discountSix : this.i18n.discountThree)}</span><strong>−${this.money(saving)}</strong></div><div class="gs-bundle__total-line gs-bundle__total-line--strong"><span>${this.escape(this.i18n.total || 'Total')}</span><span>${this.money(total)}</span></div>`
        : this.offerPilot
        ? `<div class="gs-bundle__total-line gs-bundle__total-line--strong"><span>${this.escape(this.i18n.subtotal)}</span><span>${this.money(subtotal)}</span></div>` : complete
        ? `<div class="gs-bundle__total-line"><span>${this.escape(this.i18n.three_canvases || '3 toiles')}</span><s>${this.money(subtotal)}</s></div><div class="gs-bundle__total-line"><span>2+1</span><strong>−${this.money(saving)}</strong></div><div class="gs-bundle__total-line gs-bundle__total-line--strong"><span>${this.escape(this.i18n.total || 'Total')}</span><span>${this.money(total)}</span></div>`
        : `<div class="gs-bundle__total-line gs-bundle__total-line--strong"><span>${this.escape(this.i18n.total || 'Total')}</span><span>${this.money(subtotal)}</span></div>`;
      const missing = this.targetCount - this.slots.length;
      this.atc.disabled = !complete || !!this._adding;
      this.atc.textContent = complete ? (this.offerPilot ? `${this.i18n.addTarget.replace('%COUNT%', this.targetCount)} — ${this.money(total)}` : `${this.i18n.addThree} — ${this.money(total)}`) : missing === 1 ? this.i18n.addMissingOne : this.i18n.addMissingMany.replace('%COUNT%', missing);
      this.progressEl.style.width = `${this.slots.length / this.targetCount * 100}%`;
      this.statusEl.textContent = (this.offerPilot ? this.i18n.selectedTarget.replace('%TARGET%', this.targetCount) : this.i18n.selectedCount).replace('%COUNT%', this.slots.length);
      if (this.offerPilot) {
        this.modal.dataset.offerPilot = 'true';
        this.modal.dataset.offerTarget = String(this.targetCount);
        this.modal.querySelector('h2').textContent = this.i18n.title.replace('%COUNT%', this.targetCount);
        this.modal.querySelector('.gs-bundle__eyebrow').textContent = this.targetCount === 6 ? '3 + 3' : '2 + 1';
        const fineprint = (this.inline || this.modal).querySelector('.gs-bundle__fineprint');
        fineprint.textContent = '';
        fineprint.hidden = true;
        const notice = this.notice;
        if (notice) {
          notice.hidden = !this.pendingTarget;
          notice.querySelector('[data-gs-target-message]').textContent = this.i18n.reduce.replace('%COUNT%', Math.max(0, this.slots.length - (this.pendingTarget || 3)));
          const confirm = notice.querySelector('[data-gs-target-confirm]');
          confirm.disabled = this.slots.length > (this.pendingTarget || 3);
          confirm.textContent = this.i18n.confirm;
          notice.querySelector('[data-gs-target-cancel]').textContent = this.i18n.keep;
        }
      }
    }

    optionLabel(option, values) {
      const combined = `${option.name} ${values.join(' ')}`.toLowerCase();
      return /cm|\d\s*[x×]\s*\d/.test(combined) ? this.i18n.size : /toile|châssis|chassis|canvas|frame|cadre|lienzo/.test(combined) ? this.i18n.finish : option.name;
    }

    optionRank(option, product) {
      const index = product.options.indexOf(option);
      const values = product.variants.map(variant => variant.options[index]).filter(Boolean);
      const combined = `${option.name} ${values.join(' ')}`.toLowerCase();
      if (/cm|\d\s*[x×]\s*\d/.test(combined)) return 1;
      if (/toile|châssis|chassis|canvas|frame|cadre|lienzo/.test(combined)) return 2;
      return 3;
    }

    displayOption(value, product, optionIndex) {
      const values = product.variants.map(variant => variant.options[optionIndex]).filter(Boolean);
      const combined = `${product.options[optionIndex]?.name || ''} ${values.join(' ')}`.toLowerCase();
      const mappedFinish = Number(window.GSFinishPositions[String(product.id)]) === optionIndex + 1;
      const inferredFinish = /toile|châssis|chassis|canvas|stretcher|frame|lienzo|bastidor|leinwand|keilrahmen|canvass?en|spieraam|tela|telaio/.test(combined);
      if (!mappedFinish && !inferredFinish) return String(value || '');
      const normalized = String(value || '').toLowerCase();
      if (/toile seule|canvas only|solo lienzo|lienzo solo|nur leinwand|alleen canvas|solo tela/.test(normalized)) return this.i18n.canvas_only || window.GSFinishLabels.canvas;
      if (/châssis|chassis|kit|stretcher|frame|bastidor|keilrahmen|spieraam|telaio/.test(normalized)) return this.i18n.canvas_kit || window.GSFinishLabels.kit;
      return window.GSFinishOptionLabel(product.variants, optionIndex + 1, value, String(value || ''));
    }

    buildCartItems(bundleId) {
      if (this.slots.length !== this.targetCount || this.slots.some(slot => !slot.variant.available)) return [];
      const bundleName = document.documentElement.lang.startsWith('fr') ? 'Trio 2+1 — 3 toiles' : '2+1 Trio — 3 canvases';
      // Two genuine trio groups in one batch: the existing cart classifier excludes
      // a single group marked 3+3. These properties never establish a discount.
      return this.slots.map((slot, index) => ({ id: slot.variant.id, quantity: 1, properties: {
        _bundle_id: this.targetCount === 6 ? `${bundleId}-${Math.floor(index / 3) + 1}` : bundleId,
        _bundle_type: '2+1',
        _bundle_name: bundleName,
        _bundle_url: window.location.pathname,
        _bundle_component_count: '3',
        _bundle_slot: String(index % 3 + 1)
      } }));
    }

    async addToCart() {
      if (this._adding || this.slots.length !== this.targetCount || this.atc.disabled) return;
      const bundleId = `gs-2plus1-${Date.now()}`;
      const items = this.buildCartItems(bundleId);
      if (!items.length) return;
      this._adding = true;
      this.hideError();
      this.atc.disabled = true;
      const previous = this.atc.textContent;
      this.atc.textContent = this.i18n.adding;
      try {
        const response = await fetch((window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
        const data = await response.json();
        if (!response.ok || data.status) throw data;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: this.targetCount === 6 ? 'gs_six_canvas_composition_added' : 'gs_2plus1_bundle_added', bundle_id: bundleId, component_count: this.targetCount, total_value: this.slots.reduce((sum, slot) => sum + slot.variant.price, 0) / 100 });
        this.slots = [];
        this.pendingTarget = null;
        this._adding = false;
        this.renderSummary();
        this.renderGrid();
        this.close();
        document.dispatchEvent(new CustomEvent('cart:build'));
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { bubbles: true, detail: { product: data, source: 'gs-2plus1-bundle' } }));
        document.dispatchEvent(new CustomEvent('cart:open'));
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'gs-2plus1-bundle' } }));
      } catch (error) {
        console.error('[GS Motion trio] Add to cart failed', error);
        this.showError((error && (error.description || error.message)) || (this.targetCount === 6 ? this.i18n.cartErrorSix : this.i18n.cartError));
        this.atc.disabled = false;
        this.atc.textContent = previous;
      } finally {
        this._adding = false;
      }
    }

    showError(message) { this.errorEl.textContent = message; this.errorEl.hidden = false; }
    hideError() { this.errorEl.hidden = true; this.errorEl.textContent = ''; }
    money(cents) { try { return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', { style: 'currency', currency: this.currency, minimumFractionDigits: 2 }).format((cents || 0) / 100); } catch { return `${((cents || 0) / 100).toFixed(2)} ${this.currency}`; } }
    themeAssetUrl(filename) {
      const sample = Object.values(this.thumbnailMap || {})[0];
      return sample ? String(sample).replace(/[^/?]+(?:\?.*)?$/, filename) : '';
    }
    resizeImage(url, width) { return String(url || '').replace(/(\.[a-zA-Z]{3,4})(\?.*)?$/, `_${width}x$1$2`); }
    escape(value) { const node = document.createElement('div'); node.textContent = value == null ? '' : String(value); return node.innerHTML; }
    escapeAttr(value) { return this.escape(value).replace(/"/g, '&quot;'); }
  }
  customElements.define('gs-bundle-builder', GsBundleBuilder);
}
